import { useCallback, useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PassFailEvalConfig from './components/pass-fail-eval-config';
import ScoreEvalConfig from './components/score-eval-config';
import ReferenceOutputEvalConfig from './components/reference-output-eval-config';
import {
  GoalAchievementTab,
  OutputQualityTab,
  //FactualityTab,
  //ToolUseTab,
  QuantitativeCheckTab,
  TAB_KEY_FOR_TYPE,
} from './components/dimensional-eval-config';
import type { EvalV2Type } from '@/types/eval-v2-types';
import { getEvaluations } from '@/services/evaluation/get-evaluations';
import { updateEvaluation } from '@/services/evaluation/update-evaluation';
import { createEvaluation } from '@/services/evaluation/create-evaluation';
import {
  evalV2ToEval,
  getEvalsV2,
  isV2Eval,
  createEvalV2,
  updateEvalV2,
} from '@/services/evaluation/eval-v2';
import type { CreateEvalRequest, Eval } from '@/types/evals-types';
import type {
  CreateEvalV2Request,
  UpdateEvalV2Request,
} from '@/types/eval-v2-types';
import { EVAL_NAME_MAX_LENGTH } from './components/base-eval-config';
import { useNotification } from '@/components/notifications';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';
import { sortByUpdatedAt } from '@/utils/sort-utils';
import { LoadingSpinner } from '@/components/loading-spinner';
import { generatePath, useParams } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';

// Sample data for initial evals
const initialEvals: Eval[] = [];

// pass_fail and score evals are V1 concepts: they live in the V1 DynamoDB
// table and the agentic-backend EvalsNode partitioner only finds them via
// `eval_id in v1_ids` -> /evaluations/test/run/.... All other types
// (exemplar / dimensional / quantitative_check) are V2-only. Routing
// pass_fail/score to V2 strands them as orphans on workflow runs.
const V1_EVAL_TYPES: ReadonlySet<string> = new Set(['pass_fail', 'score']);
const isV1EvalType = (type: string | undefined): boolean =>
  !!type && V1_EVAL_TYPES.has(type);

const tabForType = (type: string): string => {
  return TAB_KEY_FOR_TYPE[type as EvalV2Type] ?? type;
};

export default function EvaluationDashboard() {
  const [evals, setEvals] = useState<Eval[]>(initialEvals);
  const [selectedEval, setSelectedEval] = useState<Eval>(initialEvals[0]);
  const [activeTab, setActiveTab] = useState('pass_fail');
  const notification = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const { evalId } = useParams();
  const navigate = useOrgNavigate();
  const { currentOrg } = useOrgStore();
  const [previousOrgId, setPreviousOrgId] = useState<string | null>(null);

  const fetchEvals = useCallback(async () => {
    setIsLoading(true);
    try {
      // Merge v1 and v2 eval-config records so the page can pick the right
      // tab regardless of which table the eval came from.
      const [v1Evals, v2Evals] = await Promise.all([
        getEvaluations(),
        getEvalsV2(),
      ]);
      const sortedEvals = sortByUpdatedAt([
        ...v1Evals,
        ...v2Evals.map(evalV2ToEval),
      ]);
      setEvals(sortedEvals);

      if (evalId) {
        if (evalId === 'new') {
          const newEval: Eval = {
            id: Date.now().toString(),
            name: 'New evaluation',
            type: 'pass_fail',
            description: '',
            is_new: true,
          };
          setEvals([newEval, ...sortedEvals]);
          setSelectedEval(newEval);
          setActiveTab('pass_fail');
        } else {
          const selected = sortedEvals.find((e) => e.id === evalId);
          if (selected) {
            setSelectedEval(selected);
            setActiveTab(tabForType(selected.type));
          }
        }
      } else if (sortedEvals.length > 0) {
        // Navigate to the first evaluation if no specific evaluation is selected
        navigate(`${ROUTES.EVALUATION.EVALUATIONS}/${sortedEvals[0].id}`);
      } else {
        const defaultEval = sortedEvals[0] || initialEvals[0];
        setSelectedEval(defaultEval);
        setActiveTab(defaultEval?.type || 'pass_fail');
      }
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      setEvals(initialEvals);
      setSelectedEval(initialEvals[0]);
      setActiveTab(initialEvals[0]?.type || 'pass_fail');
    } finally {
      setIsLoading(false);
    }
  }, [evalId, navigate]);

  // Reset evalId parameter when organization changes
  useEffect(() => {
    if (currentOrg) {
      const orgHasChanged =
        previousOrgId !== null && previousOrgId !== currentOrg.orgid;

      if (orgHasChanged && evalId) {
        // Navigate to evaluations root to reset evalId parameter
        navigate(ROUTES.EVALUATION.EVALUATIONS);
      }

      setPreviousOrgId(currentOrg.orgid);
    }
  }, [currentOrg, previousOrgId, evalId, navigate]);

  useEffect(() => {
    fetchEvals();
  }, [fetchEvals]);

  const handleAddEval = () => {
    navigate(
      generatePath(ROUTES.EVALUATION.EVALUATION_SETTINGS, { evalId: 'new' }),
    );
  };

  const handleSaveEval = async (evalData: Partial<Eval>) => {
    try {
      let response;
      if (selectedEval.is_new) {
        if (isV1EvalType(evalData.type)) {
          // V1: pass_fail / score live in the V1 evals table so the
          // EvalsNode v1_ids lookup matches them at run time.
          const v1Body: CreateEvalRequest = {
            name: evalData.name!,
            type: evalData.type!,
            description: evalData.description ?? '',
            model: evalData.model,
            eval_config: evalData.eval_config,
          };
          response = {
            ...(await createEvaluation(v1Body)),
            is_v2: false,
          };
        } else {
          // V2: exemplar + dimensional types (goal_achievement,
          // output_quality, factuality, tool_use, quantitative_check).
          const v2Body: CreateEvalV2Request = {
            name: evalData.name!,
            type: evalData.type! as CreateEvalV2Request['type'],
            description: evalData.description,
            judge_model: evalData.model ?? null,
            eval_config: evalData.eval_config ?? {},
          };
          const created = await createEvalV2(v2Body);
          response = evalV2ToEval(created);
        }
      } else if (isV2Eval(selectedEval)) {
        // Update existing v2 record.
        const v2Body: UpdateEvalV2Request = {
          name: evalData.name,
          description: evalData.description,
          judge_model: evalData.model ?? null,
          eval_config: evalData.eval_config,
        };
        const updated = await updateEvalV2(selectedEval.id, v2Body);
        response = evalV2ToEval(updated);
      } else {
        // Update existing v1 record.
        response = await updateEvaluation(selectedEval.id, evalData as Eval);
      }

      const updatedEvals = evals.map((e) =>
        e.id === selectedEval.id ? { ...e, ...response, is_new: false } : e,
      );
      notification.success('Evaluation saved successfully');

      setEvals(updatedEvals);
      setSelectedEval({ ...selectedEval, ...response, is_new: false });
      navigate(
        generatePath(ROUTES.EVALUATION.EVALUATION_SETTINGS, {
          evalId: response.id,
        }),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save evaluation';
      notification.error(message);
      console.error('Error saving evaluation:', error);
    }
  };

  const DUPLICATE_SUFFIX = ' (Copy)';

  const handleDuplicateEval = () => {
    const baseName = selectedEval.name;
    const maxBaseLength = EVAL_NAME_MAX_LENGTH - DUPLICATE_SUFFIX.length;
    const duplicatedName =
      baseName.length <= maxBaseLength
        ? `${baseName}${DUPLICATE_SUFFIX}`
        : `${baseName.slice(0, maxBaseLength)}${DUPLICATE_SUFFIX}`;
    const duplicatedEval: Eval = {
      ...selectedEval,
      id: Date.now().toString(),
      name: duplicatedName,
      is_new: true,
    };
    setEvals([duplicatedEval, ...evals]);
    setSelectedEval(duplicatedEval);
  };

  const handleDeleteEval = () => {
    navigate(ROUTES.EVALUATION.EVALUATIONS);
  };

  return (
    <div className='w-full'>
      <div className='flex-1 p-4'>
        {isLoading ? (
          <div className='flex items-center justify-center h-full'>
            <LoadingSpinner />
          </div>
        ) : !evals || evals.length === 0 ? (
          <div className='flex flex-col items-center justify-center h-full text-center'>
            <h2 className='text-xl font-semibold mb-2'>No Evaluations Yet</h2>
            <p className='text-muted-foreground mb-6'>
              Get started by creating your first evaluation.
            </p>
            <Button onClick={handleAddEval}>
              <PlusCircle className='h-4 w-4' />
              New evaluation
            </Button>
          </div>
        ) : (
          <>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {selectedEval?.is_new && (
                <TabsList className='mb-6 flex-wrap h-auto'>
                  <TabsTrigger value='pass_fail'>Pass/Fail</TabsTrigger>
                  <TabsTrigger value='score'>Score</TabsTrigger>
                  <TabsTrigger value='reference_output'>
                    Reference Output
                  </TabsTrigger>
                  <TabsTrigger value='goal_achievement'>
                    Goal Achievement
                  </TabsTrigger>
                  <TabsTrigger value='output_quality'>
                    Output Quality
                  </TabsTrigger>
                  {/* <TabsTrigger value='factuality'>Factuality</TabsTrigger> */}
                  {/* <TabsTrigger value='tool_use'>Tool Use</TabsTrigger> */}
                  <TabsTrigger value='quantitative_check'>
                    Quantitative Check
                  </TabsTrigger>
                </TabsList>
              )}

              <TabsContent value='pass_fail'>
                <PassFailEvalConfig
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>

              <TabsContent value='score'>
                <ScoreEvalConfig
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>

              <TabsContent value='reference_output'>
                <ReferenceOutputEvalConfig
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>

              <TabsContent value='goal_achievement'>
                <GoalAchievementTab
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>

              <TabsContent value='output_quality'>
                <OutputQualityTab
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>

              {/* Factuality tab disabled — backend results not yet stable.
              <TabsContent value='factuality'>
                <FactualityTab
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>
              */}
              {/* Stub keeps existing factuality evals from showing a blank panel */}
              <TabsContent value='factuality'>
                <p className='text-sm text-muted-foreground py-4'>
                  Factuality evaluation is temporarily unavailable while
                  improvements are in progress.
                </p>
              </TabsContent>

              {/* Tool Use tab disabled — backend results not yet stable.
              <TabsContent value='tool_use'>
                <ToolUseTab
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>
              */}
              {/* Stub keeps existing tool_use evals from showing a blank panel */}
              <TabsContent value='tool_use'>
                <p className='text-sm text-muted-foreground py-4'>
                  Tool Use evaluation is temporarily unavailable while
                  improvements are in progress.
                </p>
              </TabsContent>

              <TabsContent value='quantitative_check'>
                <QuantitativeCheckTab
                  evalData={selectedEval}
                  onSave={handleSaveEval}
                  onDuplicate={handleDuplicateEval}
                  onDelete={handleDeleteEval}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
